// Localização: frontend/src/components/Financeiro/ParcelasManager.jsx
// Componente para gerenciar parcelas de contas a pagar/receber

import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Calendar, DollarSign } from 'lucide-react';
import PrimaryButton from '../Common/PrimaryButton';

const ParcelasManager = ({
    valorTotal,
    parcelas,
    onChange,
    disabled = false
}) => {
    const [listaParcelas, setListaParcelas] = useState([]);
    const [numeroParcelas, setNumeroParcelas] = useState(2);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (parcelas && parcelas.length > 0) {
            setListaParcelas(parcelas);
        }
    }, [parcelas]);

    // Gerar parcelas automaticamente
    const gerarParcelas = () => {
        if (!valorTotal || parseFloat(valorTotal) <= 0) {
            setErrors({ geral: 'Informe o valor total antes de gerar parcelas' });
            return;
        }

        if (numeroParcelas < 2 || numeroParcelas > 120) {
            setErrors({ numeroParcelas: 'Número de parcelas deve estar entre 2 e 120' });
            return;
        }

        const total = parseFloat(valorTotal);
        const valorParcela = total / numeroParcelas;
        const dataBase = new Date();

        const novasParcelas = [];
        for (let i = 0; i < numeroParcelas; i++) {
            const dataVencimento = new Date(dataBase);
            dataVencimento.setMonth(dataVencimento.getMonth() + i + 1);

            // Ajustar último valor para compensar arredondamentos
            const valor = i === numeroParcelas - 1
                ? (total - (valorParcela * (numeroParcelas - 1))).toFixed(2)
                : valorParcela.toFixed(2);

            novasParcelas.push({
                numero: i + 1,
                data_vencimento: dataVencimento.toISOString().split('T')[0],
                valor_parcela: valor
            });
        }

        setListaParcelas(novasParcelas);
        onChange(novasParcelas);
        setErrors({});
    };

    // Adicionar parcela manualmente
    const adicionarParcela = () => {
        const novaParcela = {
            numero: listaParcelas.length + 1,
            data_vencimento: '',
            valor_parcela: ''
        };
        const novaLista = [...listaParcelas, novaParcela];
        setListaParcelas(novaLista);
        onChange(novaLista);
    };

    // Remover parcela
    const removerParcela = (index) => {
        const novaLista = listaParcelas.filter((_, i) => i !== index);
        // Renumerar parcelas
        const listaRenumerada = novaLista.map((p, i) => ({ ...p, numero: i + 1 }));
        setListaParcelas(listaRenumerada);
        onChange(listaRenumerada);
    };

    // Atualizar parcela
    const atualizarParcela = (index, field, value) => {
        const novaLista = [...listaParcelas];
        novaLista[index] = { ...novaLista[index], [field]: value };
        setListaParcelas(novaLista);
        onChange(novaLista);
    };

    // Calcular totais
    const calcularTotais = () => {
        const soma = listaParcelas.reduce((acc, p) => acc + parseFloat(p.valor_parcela || 0), 0);
        const diferenca = parseFloat(valorTotal || 0) - soma;
        return { soma, diferenca };
    };

    const { soma, diferenca } = calcularTotais();
    const temDiferenca = Math.abs(diferenca) > 0.01;

    return (
        <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                    Gerenciar Parcelas
                </h3>
                <span className="text-sm text-gray-600">
                    {listaParcelas.length} parcela(s)
                </span>
            </div>

            {/* Gerador Automático */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Gerar Parcelas Automaticamente
                </h4>
                <div className="flex gap-3 items-end">
                    <div className="flex-1">
                        <label className="block text-xs text-gray-600 mb-1">
                            Número de Parcelas
                        </label>
                        <input
                            type="number"
                            min="2"
                            max="120"
                            value={numeroParcelas}
                            onChange={(e) => setNumeroParcelas(parseInt(e.target.value) || 2)}
                            disabled={disabled}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                        {errors.numeroParcelas && (
                            <p className="mt-1 text-xs text-red-600">{errors.numeroParcelas}</p>
                        )}
                    </div>
                    <PrimaryButton
                        type="button"
                        onClick={gerarParcelas}
                        disabled={disabled}
                        className="flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Gerar
                    </PrimaryButton>
                </div>
                {errors.geral && (
                    <p className="mt-2 text-sm text-red-600">{errors.geral}</p>
                )}
            </div>

            {/* Lista de Parcelas */}
            {listaParcelas.length > 0 && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-700">
                            Parcelas Configuradas
                        </h4>
                        <button
                            type="button"
                            onClick={adicionarParcela}
                            disabled={disabled}
                            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
                        >
                            <Plus className="w-4 h-4" />
                            Adicionar Parcela
                        </button>
                    </div>

                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {listaParcelas.map((parcela, index) => (
                            <div
                                key={index}
                                className="bg-white p-3 rounded-lg border border-gray-200 flex gap-3 items-start"
                            >
                                <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                                    <span className="text-sm font-semibold text-emerald-700">
                                        {parcela.numero}
                                    </span>
                                </div>

                                <div className="flex-1 grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">
                                            <Calendar className="w-3 h-3 inline mr-1" />
                                            Vencimento
                                        </label>
                                        <input
                                            type="date"
                                            value={parcela.data_vencimento}
                                            onChange={(e) => atualizarParcela(index, 'data_vencimento', e.target.value)}
                                            disabled={disabled}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-600 mb-1">
                                            <DollarSign className="w-3 h-3 inline mr-1" />
                                            Valor
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            value={parcela.valor_parcela}
                                            onChange={(e) => atualizarParcela(index, 'valor_parcela', e.target.value)}
                                            disabled={disabled}
                                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                            placeholder="0.00"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={() => removerParcela(index)}
                                    disabled={disabled}
                                    className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Remover parcela"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Resumo */}
                    <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                        <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Valor Total:</span>
                                <span className="font-semibold text-gray-900">
                                    R$ {parseFloat(valorTotal || 0).toFixed(2)}
                                </span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Soma das Parcelas:</span>
                                <span className={`font-semibold ${temDiferenca ? 'text-red-600' : 'text-emerald-600'}`}>
                                    R$ {soma.toFixed(2)}
                                </span>
                            </div>
                            {temDiferenca && (
                                <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                                    <span className="text-red-600 font-medium">Diferença:</span>
                                    <span className="font-semibold text-red-600">
                                        R$ {Math.abs(diferenca).toFixed(2)}
                                        {diferenca > 0 ? ' (faltando)' : ' (excedente)'}
                                    </span>
                                </div>
                            )}
                        </div>
                        {temDiferenca && (
                            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                ⚠️ A soma das parcelas deve ser igual ao valor total
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParcelasManager;
